import { Experiment, Source } from '@amplitude/experiment-js-client';

import getAmplitudeExperimentKeyToVariantMap from 'chairisher/context/experiments';

import { getAmplitudeDeploymentKeyClient } from 'chairisher/context/analytics';
import { handleAsyncError } from 'chairisher/util/logging';

let experimentClient = null;

/**
 * The keys for the client-side Experiments that are actively running in the Amplitude dashboard
 *
 * @enum {string}
 */
export const ExperimentKey = {
    FAVORITE_ACTION_COPY_TEST: 'favorite-action-copy-test', // @todo (CHAIR-18433) - remove when test is complete
};

/**
 * The values in this enum must match those that are configured in the Amplitude dashboard for all Experiments
 *
 * @enum {string}
 */
const ExperimentVariant = {
    A: 'control',
    B: 'treatment',
    C: 'treatmentb',
};

/**
 * Initializes and sets the Amplitude Experiments client with the values rendered by the server.
 */
export function initializeExperiments() {
    try {
        const deploymentKey = getAmplitudeDeploymentKeyClient();
        const experimentKeyToVariantMap = getAmplitudeExperimentKeyToVariantMap();
        experimentClient = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
            initialVariants: experimentKeyToVariantMap,
            source: Source.InitialVariants,
        });
    } catch (error) {
        console.error('Failed to initialize Amplitude Experiments client:', error);
        handleAsyncError(error);
    }
}

/**
 * Returns the variant object for an experiment based on the provided key.
 *
 * @param {string} key The key to identify the experiment
 * @returns {object|null} The variant object for the experiment, or null if the experiment client is not initialized or an error occurs
 */
export function getExperimentVariantObj(key) {
    try {
        if (!experimentClient) {
            console.error('Amplitude Experiments client not initialized');
            return null;
        }

        return experimentClient.variant(key);
    } catch (error) {
        console.error('Failed to get experiment variant:', error);
        handleAsyncError(error);
        return null;
    }
}

/**
 * Checks if the user is assigned a specific experiment variant value.
 *
 * @param {string} key The key to identify the experiment
 * @param {string} value The value of the experiment variant to check
 * @returns {boolean} True indicates the user is assigned the value, false otherwise
 */
function isExperimentVariant(key, value) {
    return getExperimentVariantObj(key)?.value === value;
}

/**
 * Checks if the user is in variant b for a given experiment.
 *
 * @param {string} key The key to identify the experiment
 * @returns {boolean} True indicates the user is in experiment variant b, false otherwise
 */
export function isExperimentVariantB(key) {
    return isExperimentVariant(key, ExperimentVariant.B);
}
